import React from "react";
import PropTypes from "prop-types";
import "../../../../styles/components/Calendar.scss";
import { css, Debouncer } from "../../../../utils/utils";
import ButtonsConstructor from "../../ButtonsConstructor";
import is from "../../../../utils/is";
import InputBase from "../Base/InputBase";

const E_ViewOptions = {
	DAYS: "DAYS",
	MONTHS: "MONTHS",
	YEARS: "YEARS",
};

class Calendar extends InputBase {
	constructor(props) {
		super(props);

		this.state = {
			...super.state,
			activeView: props.activeView,
			viewportDate: props.defaultValue ? new Date(props.defaultValue) : new Date(),
			value: props.defaultValue && new Date(props.defaultValue),
		};

		this.scrollDebouncer = new Debouncer(200);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		super.componentDidUpdate(prevProps, prevState, snapshot);
		const {refreshContent} = this.props;
		const {value} = this.state;

		if(!is.equal(value, prevState.value)) {
			this.setState({viewportDate: new Date(value)});
		}

		if(!is.equal(refreshContent, prevProps.refreshContent)) {
			this.setState({activeView: this.props.activeView, viewportDate: value ? new Date(value) : new Date()});
		}
	}

	render() {
		const {className, path, allowedDepth, onCloseTippy} = this.props;
		let {activeView, viewportDate, value} = this.state;
		viewportDate = viewportDate || new Date(); //Prevent undefined
		viewportDate = new Date(viewportDate); //Prevent ISO conversion type mismatch

		let viewportYear = viewportDate.getFullYear();
		let viewportMonth = viewportDate.toLocaleDateString(window.translator.getActiveLanguage(true), {month: "long"});
		let monthDays = this._generateMonthDays(viewportDate);
		let currentMonthDayOffset = viewportDate.modify({day: 1}).getDay();
		currentMonthDayOffset = currentMonthDayOffset == 0 ? 6 : currentMonthDayOffset - 1;

		let prevMonthDays = this._generateMonthDays(viewportDate.offset({month: -1}), {isInPast: true, isAnotherMonth: true});
		let nextMonthDays = this._generateMonthDays(viewportDate.offset({month: 1}), {isAnotherMonth: true});
		let relevantPrevMonth = prevMonthDays.splice(prevMonthDays.length - Math.max(0, currentMonthDayOffset), 99);

		monthDays = [
			...relevantPrevMonth,
			...monthDays,
			...nextMonthDays.splice(0, 42 - (relevantPrevMonth.length + monthDays.length))
		];

		let months = this._generateMonths();
		let years = this._generateYears(viewportDate);


		return (
			<section className={`calendar ${className}`}>
				<div className={"backdrop"} onClick={() => onCloseTippy()} />
				<ButtonsConstructor wrapInNav={true} className={"navigation"} buttons={[
					{
						icon: "calendar-day",
						style: css(`margin-right: auto;`),
						action: () => {
							this._changeView(E_ViewOptions.DAYS);
							this.setState({viewportDate: new Date()});
						},
						active: is.sameMonth(viewportDate, new Date())
					},
					activeView !== E_ViewOptions.YEARS && {
						text: (
							<label>
								{activeView == E_ViewOptions.DAYS && viewportMonth}&nbsp;
								{viewportYear}
							</label>
						),
						style: css(`margin: 0 auto`),
						action: () => this._changeView(activeView == E_ViewOptions.DAYS ? E_ViewOptions.MONTHS : E_ViewOptions.YEARS),
					},
					{
						icon: "chevron-left",
						style: css(`margin-left: auto;`),
						action: () => {
							switch (activeView) {
								case E_ViewOptions.DAYS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({month: -1})}));
								case E_ViewOptions.MONTHS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({year: -1})}));
								case E_ViewOptions.YEARS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({year: -16})}));
							}
						},
					},
					{
						icon: "chevron-right",
						action: () => {
							switch (activeView) {
								case E_ViewOptions.DAYS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({month: 1})}));
								case E_ViewOptions.MONTHS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({year: 1})}));
								case E_ViewOptions.YEARS:
									return this.setState(state => ({viewportDate: state.viewportDate.offset({year: 16})}));
							}
						},
					}
				]}/>

				<div className={"years-wrapper"} hidden={activeView !== E_ViewOptions.YEARS} name={"years"} ref={this.setRef}>
					<div className={"grid-table no-label-content years-grid"} onWheel={e => this._handleScroll(e.deltaY, 'year', 16)}>
						<ButtonsConstructor buttons={years.map((year) => ({
							text: year.number,
							textTranslated: true,
							action: () => {
								allowedDepth !== E_ViewOptions.YEARS && this._changeView(E_ViewOptions.MONTHS);
								let date = this.state.viewportDate.modify({year: year.number});
								this.setState({viewportDate: date});

								if(allowedDepth == E_ViewOptions.YEARS) {
									setTimeout(() => this.applyChange(path, date), 0);
								}
							}
						}))} />
					</div>
				</div>

				<div className={"months-wrapper"} hidden={activeView !== E_ViewOptions.MONTHS} name={"months"} ref={this.setRef}>
					<div className={"grid-table no-label-content months-grid"} onWheel={e => this._handleScroll(e.deltaY, 'year')}>
						<ButtonsConstructor buttons={months.map((month, i) => ({
							text: month.label,
							textTranslated: true,
							action: () => {
								allowedDepth !== E_ViewOptions.MONTHS && this._changeView(E_ViewOptions.DAYS);
								let date = this.state.viewportDate.modify({month: i});
								this.setState({viewportDate: date});

								if(allowedDepth == E_ViewOptions.MONTHS) {
									setTimeout(() => this.applyChange(path, date), 0);
								}
							}
						}))} />
					</div>
				</div>

				<div className={"days-wrapper"} hidden={activeView !== E_ViewOptions.DAYS} name={"days"} ref={this.setRef}>
					<div className={"grid-table no-label-content days-grid"} onWheel={e => this._handleScroll(e.deltaY, 'month')}>
						{
							Array(7).fill(null).map((day, i) => (
								<label key={i} className={`${i == 6 && "sunday"}`}>
									{
										new Date(null).offset({
											day: 4 + i
										}).toLocaleDateString(
											window.translator.getActiveLanguage(true),
											{weekday: 'short'}
										)
									}
								</label>
							))
						}
						<ButtonsConstructor buttons={monthDays.map(day => {
							return {
								text: day.number,
								className: `${day.isAnotherMonth && "another-month"} ${day.isToday && "today"} ${day.dayKey === "SUNDAY" && "sunday"}`,
								action: () => this.applyChange(path, day.date),
								active: is.sameDay(value, day.date),
							}
						})} />
					</div>
				</div>
			</section>
		)
	}

	_generateMonthDays(viewportDate = new Date(), sharedModifiers) {
		return Array(viewportDate.getMonthLength() || 0).fill(null).map((_, i) => {
			let iterDay = viewportDate.modify({day: i + 1});
			let isToday = is.today(iterDay);
			let dayKey = iterDay.getDayKey();

			return {
				number: i + 1, // Day in month number, 1 - 31
				dayKey: dayKey, // Day in week key (MONDAY - SUNDAY)
				date: iterDay, // Date
				isInPast: is.past(iterDay) && !isToday,
				isToday: isToday,
				isActive: false,
				...sharedModifiers,
			};
		});
	}

	_generateMonths(sharedModifiers) {
		return Array(12).fill(null).map((_, i) => {
			let iterMonth = new Date(null).offset({month: i});
			return {
				month: i,
				label: iterMonth.toLocaleDateString(window.translator.getActiveLanguage(true), {month: 'short'}),
				...sharedModifiers
			};
		});
	}

	_generateYears(viewportDate, sharedModifiers) {
		return Array(16).fill(null).map((_, i) => {
			let iterYear = new Date(viewportDate).offset({year: i});
			return {
				number: parseInt(iterYear.getFullYear()),
				...sharedModifiers
			};
		});
	}

	_changeView(target) {
		const {activeView} = this.state;
		if(target == activeView) {return;}

		let levels = [E_ViewOptions.YEARS, E_ViewOptions.MONTHS, E_ViewOptions.DAYS];
		let refs = {
			[E_ViewOptions.YEARS]: this.years,
			[E_ViewOptions.MONTHS]: this.months,
			[E_ViewOptions.DAYS]: this.days,
		};

		let activeTransition = levels.indexOf(activeView) < levels.indexOf(target) ? "scale-in" : "scale-out";
		let newTransition = activeTransition == "scale-in" ? "scale-out" : "scale-in";

		refs[activeView].classList.add(activeTransition);
		setTimeout(() => {
			this.setState({activeView: target});
			refs[activeView].classList.remove(activeTransition);
			refs[target].classList.add(newTransition);
			setTimeout(() => {
				refs[target].classList.remove(newTransition);
			}, 200);
		}, 100);
	}

	_handleScroll(delta, datePart, step = 1) {
		this.scrollDebouncer.call(() => {
			this.setState(state => ({viewportDate: state.viewportDate.offset({[datePart]: (delta / 100) * step})}))
		});
	}

	static get propTypes() {
		return {
			...super.propTypes,
			options: PropTypes.array, //Props propagation error prevention. Irrelevant with this component.
			allowedDepth: PropTypes.string, /** @type {E_ViewOptions} **/
			onCloseTippy: PropTypes.func //If the calendar is wrapped inside a tippy wrapper, there is an option to close it (click on backdrop handle)
		}
	}

	static get stateTypes() {
		return {
			...super.stateTypes,
			activeView: PropTypes.string,
			viewportDate: PropTypes.object,
		}
	}

	static get defaultProps() {
		return {
			...super.defaultProps,
			activeView: E_ViewOptions.DAYS,
			onCloseTippy: () => null,
		}
	}
}

export default Calendar;
